import './styles.scoped.css'
import {useEffect, useRef, useState} from 'react'
import {Box, Card, Typography, Chip, IconButton, Divider, Skeleton} from '@mui/material'
import {WarningAmberOutlined, AccessTimeOutlined, ChevronRightRounded} from '@mui/icons-material'
import {useMobile} from '../../../../hooks'
import {getColorFromName, getInitials, ignoreTimezone, parseError, formatTime, capitalizeFirst} from '../../../../utils/helpers'
import {dashboardApi} from '../../../../services'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {format, formatDistanceToNow} from 'date-fns'
import {useNavigate} from 'react-router-dom'
import {SkeletonContainer, TextSkeleton, ChipSkeleton} from '../../../../components'

const titleWidths = [240, 200, 280]
const subtitleWidths = [380, 320, 420]

const AttentionItemsSkeleton = ({mobile, count = 3}) => (
    <SkeletonContainer>
        {Array.from({length: count}).map((_, index) => (
            <div key={index}>
                <br />
                {mobile ? (
                    <div className="row-content">
                        <Skeleton variant="rounded" className="icon" />
                        <div className="column-content">
                            <TextSkeleton width={titleWidths[index] - 60} fontSize={14} />
                            <TextSkeleton width={subtitleWidths[index] - 160} fontSize={12} />
                            <div className="row-content">
                                <Skeleton variant="circular" width={20} height={20} />
                                <TextSkeleton width={120} fontSize={12} />
                            </div>
                            <div className="view-more">
                                <TextSkeleton width={100} fontSize={14} />
                                <Skeleton variant="circular" width={24} height={24} />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="row">
                        <div className="left">
                            <Skeleton variant="rounded" className="icon" />
                            <div className="title">
                                <TextSkeleton width={titleWidths[index]} fontSize={20} />
                                <TextSkeleton width={subtitleWidths[index]} fontSize={14} />
                            </div>
                        </div>
                        <div className="right">
                            <Skeleton variant="circular" width={24} height={24} />
                            <TextSkeleton width={130} fontSize={16} />
                            <ChipSkeleton width={84} height={32} />
                            <div className="view-more">
                                <TextSkeleton width={110} fontSize={16} />
                                <Skeleton variant="circular" width={24} height={24} />
                            </div>
                        </div>
                    </div>
                )}
                <br />
                <Divider />
            </div>
        ))}
    </SkeletonContainer>
)

export default () => {
    const {mobile} = useMobile()
    const {setMessageBox, branchId, dashboardDate, dashboardRefresh} = useGlobalStates()
    const [selected, setSelected] = useState('all')
    const [items, setItems] = useState([])
    const [filteredItems, setFilteredItems] = useState([])
    const [counts, setCounts] = useState(null)
    const [skeleton, setSkeleton] = useState(true)
    const requestId = useRef(0)
    const navigate = useNavigate()

    // A new day, branch or Refresh replaces the list with the skeleton.
    useEffect(() => {
        setSkeleton(true)
        setItems([])
        setCounts(null)
    }, [branchId, dashboardDate, dashboardRefresh])

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (branchId && dashboardDate) {
                const id = ++requestId.current
                dashboardApi
                    .getNeedYourAttention(branchId, {date: format(dashboardDate, 'yyyy-MM-dd')})
                    .then(({data: {data}}) => {
                        if (id !== requestId.current) return
                        setItems(data?.items || [])
                        setCounts(data?.counts)
                        setSkeleton(false)
                    })
                    .catch(error => {
                        if (id !== requestId.current) return
                        const {title, description} = parseError(error)
                        if (description) {
                            setMessageBox({status: 'error', title, description})
                        }
                        setSkeleton(false)
                    })
            }
        }, 500)
        return () => clearTimeout(timeout)
    }, [branchId, dashboardDate, dashboardRefresh])

    useEffect(() => {
        if (items && selected != 'all') {
            setFilteredItems(items.filter(item => item?.severity?.toLowerCase().includes(selected.toLowerCase())))
        } else {
            setFilteredItems(items)
        }
    }, [selected, items])

    const title = item => {
        if (item?.type == 'visit_conflict') {
            return 'Duplicate Schedule Detected'
        } else if (item?.type == 'agreement_ending_soon') {
            return 'Agreements ending soon'
        } else if (item?.type == 'service_authorization_expired') {
            return 'Service authorizations expired'
        } else {
            return item?.title
        }
    }

    const subtitle = item => {
        if (item?.type == 'visit_conflict') {
            return `A visit by ${item?.caregiver_name} to ${item?.client_name} is scheduled at the same time`
        } else if (item?.type == 'agreement_ending_soon') {
            return `An agreement with ${item?.client_name} is ending soon ${formatDistanceToNow(new Date(ignoreTimezone(item?.occurred_at) ?? null), {addSuffix: true})}`
        } else if (item?.type == 'service_authorization_expired') {
            return `A service authorization with ${item?.client_name} has expired ${formatDistanceToNow(new Date(ignoreTimezone(item?.occurred_at) ?? null), {addSuffix: true})}`
        } else {
            return item?.subtitle
        }
    }

    const time = item => {
        if (item?.type == 'visit_conflict') {
            return formatTime(item?.start_time) + ' - ' + formatDistanceToNow(new Date(ignoreTimezone(item?.occurred_at) ?? null), {addSuffix: true})
        } else if (item?.type == 'agreement_ending_soon') {
            return 'soonest ' + formatDistanceToNow(new Date(ignoreTimezone(item?.occurred_at) ?? null), {addSuffix: true})
        } else if (item?.type == 'service_authorization_expired') {
            return formatDistanceToNow(new Date(ignoreTimezone(item?.occurred_at) ?? null), {addSuffix: true})
        } else {
            return formatDistanceToNow(new Date(ignoreTimezone(item?.occurred_at) ?? null), {addSuffix: true})
        }
    }

    const action = item => {
        if (item?.type == 'visit_conflict') {
            navigate(`/scheduling/${item?.schedule_id}/visits/${item?.entity_id}`)
        } else if (item?.type == 'agreement_ending_soon') {
            navigate(`/clients-management-nosublayout/${item?.client_id}/service-agreement/view-service-agreement/${item?.entity_id}`)
        } else if (item?.type == 'service_authorization_expired') {
            navigate(`/clients-management-nosublayout/${item?.client_id}/service-agreement/view-service-agreement/${item?.entity_id}`)
        } else {
            navigate('/')
        }
    }

    return (
        <div className="container">
            <br />
            {mobile ? (
                <>
                    <div className="row-content">
                        <div className="icon" style={{backgroundColor: '#D32F2F0A', width: 48, height: 48}}>
                            <WarningAmberOutlined fontSize="large" color="error" />
                        </div>
                        <Typography variant="h6" color="text.primary">
                            Needs your attention
                        </Typography>
                    </div>
                    <br />
                    <div className="row-content">
                        <div className="status" style={selected === 'all' ? {backgroundColor: '#073735'} : {}} onClick={() => setSelected('all')}>
                            <Typography variant="chipLabel" color={selected === 'all' ? '#ffffff' : 'text.primary'}>
                                All
                            </Typography>
                            {skeleton ? (
                                <Skeleton variant="rounded" width={24} height={24} sx={{borderRadius: 50}} />
                            ) : (
                                <Chip label={counts?.all || 0} size="small" color="primary" />
                            )}
                        </div>
                        <div
                            className="status"
                            style={selected === 'critical' ? {backgroundColor: '#073735'} : {}}
                            onClick={() => setSelected('critical')}>
                            <Typography variant="chipLabel" color={selected === 'critical' ? '#ffffff' : 'text.primary'}>
                                Critical
                            </Typography>
                            {skeleton ? (
                                <Skeleton variant="rounded" width={24} height={24} sx={{borderRadius: 50}} />
                            ) : (
                                <Chip label={counts?.critical || 0} size="small" color="error" />
                            )}
                        </div>
                        <div className="status" style={selected === 'high' ? {backgroundColor: '#073735'} : {}} onClick={() => setSelected('high')}>
                            <Typography variant="chipLabel" color={selected === 'high' ? '#ffffff' : 'text.primary'}>
                                High
                            </Typography>
                            {skeleton ? (
                                <Skeleton variant="rounded" width={24} height={24} sx={{borderRadius: 50}} />
                            ) : (
                                <Chip label={counts?.high || 0} size="small" color="warning" />
                            )}
                        </div>
                        <div className="status" style={selected === 'watch' ? {backgroundColor: '#073735'} : {}} onClick={() => setSelected('watch')}>
                            <Typography variant="chipLabel" color={selected === 'watch' ? '#ffffff' : 'text.primary'}>
                                Watch
                            </Typography>
                            {skeleton ? (
                                <Skeleton variant="rounded" width={24} height={24} sx={{borderRadius: 50}} />
                            ) : (
                                <Chip label={counts?.watch || 0} size="small" color="info" />
                            )}
                        </div>
                    </div>
                    <br />
                    <Divider color="#bfbfbf" />
                    <br />
                    <div className="scroll-container">
                        {skeleton && <AttentionItemsSkeleton mobile={mobile} />}
                        {filteredItems?.map((item, index) => (
                            <div key={index}>
                                <br />
                                {item?.severity == 'critical' && (
                                    <div className="row-content">
                                        <div className="icon" style={{backgroundColor: '#D32F2F0A'}}>
                                            <WarningAmberOutlined fontSize="small" color="error" />
                                        </div>
                                        <div className="column-content">
                                            <Typography variant="h9" color="text.primary">
                                                {title(item)}
                                            </Typography>
                                            <Typography variant="body3" color="text.secondary">
                                                {subtitle(item)}
                                            </Typography>
                                            <div className="row-content">
                                                <AccessTimeOutlined color="error" fontSize="small" />
                                                <Typography variant="h10" color="error">
                                                    {time(item)}
                                                </Typography>
                                            </div>
                                            <div className="view-more" onClick={() => action(item)}>
                                                <Typography variant="h9" color="primary">
                                                    {capitalizeFirst(item?.action)}
                                                </Typography>
                                                <ChevronRightRounded fontSize="medium" color="primary" />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {item?.severity == 'high' && (
                                    <div className="row-content">
                                        <div className="icon" style={{backgroundColor: '#fef9f5'}}>
                                            <AccessTimeOutlined fontSize="small" color="warning" />
                                        </div>
                                        <div className="column-content">
                                            <Typography variant="h9" color="text.primary">
                                                {title(item)}
                                            </Typography>
                                            <Typography variant="body3" color="text.secondary">
                                                {subtitle(item)}
                                            </Typography>
                                            <div className="row-content">
                                                <AccessTimeOutlined fontSize="small" style={{color: '#FF9800'}} />
                                                <Typography variant="h10" color="#FF9800">
                                                    {time(item)}
                                                </Typography>
                                            </div>
                                            <div className="view-more" onClick={() => action(item)}>
                                                <Typography variant="h9" color="primary">
                                                    {capitalizeFirst(item?.action)}
                                                </Typography>
                                                <ChevronRightRounded fontSize="medium" color="primary" />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {item?.severity == 'watch' && (
                                    <div className="row-content">
                                        <div className="icon" style={{backgroundColor: '#f5fafd'}}>
                                            <AccessTimeOutlined fontSize="small" color="info" />
                                        </div>
                                        <div className="column-content">
                                            <Typography variant="h9" color="text.primary">
                                                {title(item)}
                                            </Typography>
                                            <Typography variant="body3" color="text.secondary">
                                                {subtitle(item)}
                                            </Typography>
                                            <div className="row-content">
                                                <AccessTimeOutlined color="info" fontSize="small" />
                                                <Typography variant="h10" color="info">
                                                    {time(item)}
                                                </Typography>
                                            </div>
                                            <div className="view-more" onClick={() => action(item)}>
                                                <Typography variant="h9" color="primary">
                                                    {capitalizeFirst(item?.action)}
                                                </Typography>
                                                <ChevronRightRounded fontSize="medium" color="primary" />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <br />
                                <Divider />
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <>
                    <div className="row">
                        <div className="left">
                            <div className="icon" style={{backgroundColor: '#D32F2F0A'}}>
                                <WarningAmberOutlined fontSize="large" color="error" />
                            </div>
                            <Typography variant="h6" color="text.primary">
                                Needs your attention
                            </Typography>
                        </div>
                        <div className="right">
                            <div className="status" style={selected === 'all' ? {backgroundColor: '#073735'} : {}} onClick={() => setSelected('all')}>
                                <Typography variant="chipLabel" color={selected === 'all' ? '#ffffff' : 'text.primary'}>
                                    All
                                </Typography>
                                {skeleton ? (
                                    <Skeleton variant="rounded" width={24} height={24} sx={{borderRadius: 50}} />
                                ) : (
                                    <Chip label={counts?.all || 0} size="small" color="primary" />
                                )}
                            </div>
                            <div
                                className="status"
                                style={selected === 'critical' ? {backgroundColor: '#073735'} : {}}
                                onClick={() => setSelected('critical')}>
                                <Typography variant="chipLabel" color={selected === 'critical' ? '#ffffff' : 'text.primary'}>
                                    Critical
                                </Typography>
                                {skeleton ? (
                                    <Skeleton variant="rounded" width={24} height={24} sx={{borderRadius: 50}} />
                                ) : (
                                    <Chip label={counts?.critical || 0} size="small" color="error" />
                                )}
                            </div>
                            <div
                                className="status"
                                style={selected === 'high' ? {backgroundColor: '#073735'} : {}}
                                onClick={() => setSelected('high')}>
                                <Typography variant="chipLabel" color={selected === 'high' ? '#ffffff' : 'text.primary'}>
                                    High
                                </Typography>
                                {skeleton ? (
                                    <Skeleton variant="rounded" width={24} height={24} sx={{borderRadius: 50}} />
                                ) : (
                                    <Chip label={counts?.high || 0} size="small" color="warning" />
                                )}
                            </div>
                            <div
                                className="status"
                                style={selected === 'watch' ? {backgroundColor: '#073735'} : {}}
                                onClick={() => setSelected('watch')}>
                                <Typography variant="chipLabel" color={selected === 'watch' ? '#ffffff' : 'text.primary'}>
                                    Watch
                                </Typography>
                                {skeleton ? (
                                    <Skeleton variant="rounded" width={24} height={24} sx={{borderRadius: 50}} />
                                ) : (
                                    <Chip label={counts?.watch || 0} size="small" color="info" />
                                )}
                            </div>
                        </div>
                    </div>
                    <br />
                    <Divider color="#bfbfbf" />
                    <div className="scroll-container">
                        {skeleton && <AttentionItemsSkeleton mobile={mobile} />}
                        {filteredItems?.map((item, index) => (
                            <div key={index}>
                                <br />
                                {item?.severity == 'critical' && (
                                    <div className="row">
                                        <div className="left">
                                            <div className="icon" style={{backgroundColor: '#D32F2F0A'}}>
                                                <WarningAmberOutlined fontSize="large" color="error" />
                                            </div>
                                            <div className="title">
                                                <Typography variant="h6" color="text.primary">
                                                    {title(item)}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {subtitle(item)}
                                                </Typography>
                                            </div>
                                        </div>
                                        <div className="right">
                                            <AccessTimeOutlined color="error" />
                                            <Typography variant="h8" color="error">
                                                {time(item)}
                                            </Typography>
                                            <Chip label="CRITICAL" style={{backgroundColor: '#D32F2F0A', color: '#D32F2F'}} />
                                            <div className="view-more" onClick={() => action(item)}>
                                                <Typography variant="h8" color="primary">
                                                    {capitalizeFirst(item?.action)}
                                                </Typography>
                                                <ChevronRightRounded fontSize="medium" color="primary" />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {item?.severity == 'high' && (
                                    <div className="row">
                                        <div className="left">
                                            <div className="icon" style={{backgroundColor: '#fef9f5'}}>
                                                <AccessTimeOutlined fontSize="large" color="warning" />
                                            </div>
                                            <div className="title">
                                                <Typography variant="h6" color="text.primary">
                                                    {title(item)}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {subtitle(item)}
                                                </Typography>
                                            </div>
                                        </div>
                                        <div className="right">
                                            <AccessTimeOutlined style={{color: '#FF9800'}} />
                                            <Typography variant="h8" color="#FF9800">
                                                {time(item)}
                                            </Typography>
                                            <Chip label="HIGH" style={{backgroundColor: '#fef9f5', color: '#ED6C02'}} />
                                            <div className="view-more" onClick={() => action(item)}>
                                                <Typography variant="h8" color="primary">
                                                    {capitalizeFirst(item?.action)}
                                                </Typography>
                                                <ChevronRightRounded fontSize="medium" color="primary" />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {item?.severity == 'watch' && (
                                    <div className="row">
                                        <div className="left">
                                            <div className="icon" style={{backgroundColor: '#f5fafd'}}>
                                                <AccessTimeOutlined fontSize="large" color="info" />
                                            </div>
                                            <div className="title">
                                                <Typography variant="h6" color="text.primary">
                                                    {title(item)}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {subtitle(item)}
                                                </Typography>
                                            </div>
                                        </div>
                                        <div className="right">
                                            <AccessTimeOutlined color="info" />
                                            <Typography variant="h8" color="info">
                                                {time(item)}
                                            </Typography>
                                            <Chip label="WATCH" style={{backgroundColor: '#f5fafd', color: '#0288D1'}} />
                                            <div className="view-more" onClick={() => action(item)}>
                                                <Typography variant="h8" color="primary">
                                                    {capitalizeFirst(item?.action)}
                                                </Typography>
                                                <ChevronRightRounded fontSize="medium" color="primary" />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <br />
                                <Divider />
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    )
}
